// Custom notification service that manages styled notifications
// to replace Element UI's default green notifications

class NotificationService {
  constructor() {
    this.notifications = []
    this.container = null
    this.idCounter = 0
  }

  // Initialize the notification container in the DOM
  init() {
    if (typeof window === 'undefined') return
    
    // Add global styles for notifications if they don't exist
    if (!document.getElementById('notification-styles')) {
      const style = document.createElement('style')
      style.id = 'notification-styles'
      style.textContent = `
        .notification-close-btn:hover {
          background-color: #f1f5f9 !important;
          color: #64748b !important;
        }
        
        @media (max-width: 480px) {
          #custom-notifications-container {
            top: 16px !important;
            right: 16px !important;
            left: 16px !important;
          }
          
          #custom-notifications-container > div {
            max-width: none !important;
            min-width: none !important;
          }
        }
      `
      document.head.appendChild(style)
    }
    
    this.container = document.createElement('div')
    this.container.id = 'custom-notifications-container'
    this.container.style.cssText = `
      position: fixed;
      top: 32px;
      right: 32px;
      z-index: 3000;
      pointer-events: none;
      display: flex;
      flex-direction: column;
      gap: 16px;
    `
    document.body.appendChild(this.container)
  }

  // Show a notification
  show(options = {}) {
    if (!this.container) this.init()
    
    const notification = {
      id: ++this.idCounter,
      type: options.type || 'info',
      title: options.title || '',
      message: options.message || '',
      duration: options.duration !== undefined ? options.duration : 4000,
      ...options
    }

    this.notifications.push(notification)
    this.render(notification)
    
    // Auto-remove after duration
    if (notification.duration > 0) {
      setTimeout(() => {
        this.remove(notification.id)
      }, notification.duration)
    }

    return notification.id
  }

  // Success notification
  success(title, message = '', options = {}) {
    return this.show({
      type: 'success',
      title,
      message,
      ...options
    })
  }

  // Error notification
  error(title, message = '', options = {}) {
    return this.show({
      type: 'error',
      title,
      message,
      duration: 6000, // Longer duration for errors
      ...options
    })
  }

  // Warning notification
  warning(title, message = '', options = {}) {
    return this.show({
      type: 'warning',
      title,
      message,
      ...options
    })
  }

  // Info notification
  info(title, message = '', options = {}) {
    return this.show({
      type: 'info',
      title,
      message,
      ...options
    })
  }

  // Remove a specific notification
  remove(id) {
    const element = document.getElementById(`notification-${id}`)
    if (element) {
      element.style.opacity = '0'
      element.style.transform = 'translateX(100%) scale(0.95)'
      setTimeout(() => {
        if (element.parentNode) {
          element.parentNode.removeChild(element)
        }
      }, 300)
    }
    
    this.notifications = this.notifications.filter(n => n.id !== id)
  }

  // Clear all notifications
  clear() {
    this.notifications.forEach(notification => {
      this.remove(notification.id)
    })
    this.notifications = []
  }

  // Render a notification in the DOM
  render(notification) {
    const element = document.createElement('div')
    element.id = `notification-${notification.id}`
    element.style.cssText = `
      pointer-events: auto;
      max-width: 420px;
      min-width: 320px;
      opacity: 0;
      transform: translateX(100%) scale(0.95);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      transform-origin: top right;
    `

    const iconMap = {
      success: 'el-icon-check',
      error: 'el-icon-close',
      warning: 'el-icon-warning',
      info: 'el-icon-info'
    }

    const colorMap = {
      success: { 
        border: '#0a66c2', 
        bg: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        iconBg: 'linear-gradient(135deg, #0a66c2, #004182)',
        shadowColor: 'rgba(10, 102, 194, 0.15)'
      },
      error: { 
        border: '#ef4444', 
        bg: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
        iconBg: 'linear-gradient(135deg, #ef4444, #dc2626)',
        shadowColor: 'rgba(239, 68, 68, 0.15)'
      },
      warning: { 
        border: '#f59e0b', 
        bg: 'linear-gradient(135deg, #fffbeb 0%, #ffffff 100%)',
        iconBg: 'linear-gradient(135deg, #f59e0b, #d97706)',
        shadowColor: 'rgba(245, 158, 11, 0.15)'
      },
      info: { 
        border: '#3b82f6', 
        bg: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
        iconBg: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        shadowColor: 'rgba(59, 130, 246, 0.15)'
      }
    }

    const colors = colorMap[notification.type] || colorMap.info
    const icon = iconMap[notification.type] || iconMap.info

    element.innerHTML = `
      <div style="
        display: flex;
        align-items: flex-start;
        gap: 16px;
        padding: 20px 24px;
        background: ${colors.bg};
        border-radius: 16px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.05);
        border-left: 4px solid ${colors.border};
        position: relative;
        backdrop-filter: blur(8px);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      ">
        <div style="
          flex-shrink: 0;
          width: 32px;
          height: 32px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: white;
          margin-top: 0px;
          background: ${colors.iconBg};
          font-family: element-icons;
          box-shadow: 0 2px 4px ${colors.shadowColor};
        ">
          <i class="${icon}"></i>
        </div>
        <div style="flex: 1; min-width: 0;">
          <div style="
            font-size: 15px;
            font-weight: 600;
            color: #1e293b;
            margin-bottom: ${notification.message ? '4px' : '0'};
            line-height: 1.4;
            letter-spacing: -0.025em;
          ">${notification.title}</div>
          ${notification.message ? `
            <div style="
              font-size: 14px;
              color: #64748b;
              line-height: 1.5;
              font-weight: 500;
            ">${notification.message}</div>
          ` : ''}
        </div>
        <button onclick="window.notificationService.remove(${notification.id})" class="notification-close-btn" style="
          flex-shrink: 0;
          background: none;
          border: none;
          padding: 6px;
          cursor: pointer;
          color: #94a3b8;
          border-radius: 8px;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          margin-top: -2px;
          font-family: element-icons;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        ">
          <i class="el-icon-close"></i>
        </button>
      </div>
    `

    this.container.appendChild(element)

    // Trigger animation
    setTimeout(() => {
      element.style.opacity = '1'
      element.style.transform = 'translateX(0) scale(1)'
    }, 10)
  }
}

// Create a global instance
const notificationService = new NotificationService()

// Make it available globally for inline onclick handlers
if (typeof window !== 'undefined') {
  window.notificationService = notificationService
}

export default notificationService
